import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Typography } from '../typography';
import { Button } from '../button';
import { ModalBase, ModalWrapper, ModalFooterButtons } from './ModalComponents';
import { usePwaInstalled } from '../../hooks';
import { i18n } from '../../i18n';

export const installAppModal: any = {};
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const InstallAppModal = () => {
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = usePwaInstalled();

  useEffect(() => {
    installAppModal.open = async () => {
      if ('getInstalledRelatedApps' in navigator) {
        try {
          const relatedApps = await (navigator as any).getInstalledRelatedApps();
          if (relatedApps && relatedApps.length > 0) {
            setIsInstalled(true);
          }
        } catch (e) {
          // catch error
        }
      }

      setOpen(true);
    };

    const checkIfInstalled = () => {
      const standalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window as any).navigator.standalone === true;

      if (standalone) {
        return;
      }

      if (isMobile && !standalone) {
        const handleBeforeInstallPrompt = (e: Event) => {
          e.preventDefault();
          setDeferredPrompt(e as BeforeInstallPromptEvent);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
          window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
      }
    };

    checkIfInstalled();
  }, []);

  const handleSubmitClick = async () => {
    if (!deferredPrompt) {
      setIsInstalled(true);
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    return;
  };

  const handleOnClose = () => {
    setOpen(false);
  };

  return (
    <ModalBase
      id={'install-app-modal'}
      paperSize={'min-content'}
      open={open}
      onClose={handleOnClose}
    >
      <ModalWrapper>
        <Typography variant={'textL'} weight={'bold'}>
          {isInstalled
            ? i18n.t('pages.modal.installAppTitleSuccess')
            : i18n.t('pages.modal.installAppTitle')}
        </Typography>
        {isInstalled && (
          <Typography variant="textS" weight="medium" breakSpaces>
            {i18n.t('pages.modal.installAppMessage')}
          </Typography>
        )}
        {!isInstalled && (
          <ModalFooterButtons columns={'1fr'}>
            <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
              {i18n.t('button.cancel')}
            </Button>
            <Button size={'medium'} data-color={'primary'} onClick={handleSubmitClick}>
              {i18n.t('button.install')}
            </Button>
          </ModalFooterButtons>
        )}
      </ModalWrapper>
    </ModalBase>
  );
};
